import { useEffect, useRef } from "react";
import {
  MCP_ZONE_ACTIVATE,
  MCP_ZONE_DEACTIVATE,
  MCP_ZONE_PULSE,
  MCP_ZONE_RESET,
  McpAnimationZone
} from "../../../shared/utility/mcp-tool-bridge";
import "./mcp-canvas-border-animation.scss";

const CANVAS_ZONE: McpAnimationZone = "canvas";
const PULSE_DURATION_MS = 900;
const SAFETY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes — auto-deactivate if done event is lost
type ZoneEvent = CustomEvent<{ zone?: McpAnimationZone }>;

const getIframe = (): HTMLElement | null => document.getElementById("kuika_iframeAgent_canvas");
const getCenter = (): HTMLElement | null => document.querySelector<HTMLElement>(".kuika_center");

export const McpCanvasBackgroundAnimation: React.FC = () => {
  const count = useRef(0);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flowRef = useRef<HTMLDivElement | null>(null);
  const mutObsRef = useRef<MutationObserver | null>(null);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncOverlay = () => {
    const iframe = getIframe();
    const center = getCenter();
    const flow = flowRef.current;
    if (!iframe || !center || !flow) return;
    const iframeRect = iframe.getBoundingClientRect();
    const centerRect = center.getBoundingClientRect();
    flow.style.top = `${iframeRect.top - centerRect.top}px`;
    flow.style.left = `${iframeRect.left - centerRect.left}px`;
    flow.style.width = `${iframeRect.width}px`;
    flow.style.height = `${iframeRect.height}px`;
  };

  const activate = () => {
    const iframe = getIframe();
    const center = getCenter();
    if (!iframe || !center) return;

    if (!flowRef.current) {
      const el = document.createElement("div");
      el.className = "mcp-ai-flow-overlay";
      el.setAttribute("aria-hidden", "true");
      center.appendChild(el);

      flowRef.current = el;
      syncOverlay();

      mutObsRef.current = new MutationObserver(syncOverlay);
      mutObsRef.current.observe(iframe, { attributes: true, attributeFilter: ["style"] });
    }

    // Safety timeout — auto-deactivate if done/reset event never arrives
    if (safetyTimer.current) clearTimeout(safetyTimer.current);
    safetyTimer.current = setTimeout(() => {
      if (count.current > 0) {
        count.current = 0;
        deactivate();
      }
    }, SAFETY_TIMEOUT_MS);
  };

  const deactivate = () => {
    if (safetyTimer.current) {
      clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
    if (mutObsRef.current) {
      mutObsRef.current.disconnect();
      mutObsRef.current = null;
    }
    if (flowRef.current) {
      flowRef.current.remove();
      flowRef.current = null;
    }
  };

  useEffect(() => {
    const onActivate = (e: Event) => {
      if ((e as ZoneEvent).detail?.zone !== CANVAS_ZONE) return;
      count.current += 1;
      activate();
    };

    const onDeactivate = (e: Event) => {
      if ((e as ZoneEvent).detail?.zone !== CANVAS_ZONE) return;
      count.current = Math.max(0, count.current - 1);
      if (count.current === 0) {
        deactivate();
        if (pulseTimer.current) {
          clearTimeout(pulseTimer.current);
          pulseTimer.current = null;
        }
      }
    };

    const onPulse = (e: Event) => {
      if ((e as ZoneEvent).detail?.zone !== CANVAS_ZONE) return;
      if (count.current <= 0) return;

      const flow = flowRef.current;
      if (flow) flow.classList.add("mcp-ai-flow-overlay--pulse");

      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => {
        const flow = flowRef.current;
        if (flow) flow.classList.remove("mcp-ai-flow-overlay--pulse");
      }, PULSE_DURATION_MS);
    };

    const onReset = () => {
      count.current = 0;
      deactivate();
      if (pulseTimer.current) {
        clearTimeout(pulseTimer.current);
        pulseTimer.current = null;
      }
    };

    window.addEventListener(MCP_ZONE_ACTIVATE, onActivate);
    window.addEventListener(MCP_ZONE_DEACTIVATE, onDeactivate);
    window.addEventListener(MCP_ZONE_PULSE, onPulse);
    window.addEventListener(MCP_ZONE_RESET, onReset);

    return () => {
      window.removeEventListener(MCP_ZONE_ACTIVATE, onActivate);
      window.removeEventListener(MCP_ZONE_DEACTIVATE, onDeactivate);
      window.removeEventListener(MCP_ZONE_PULSE, onPulse);
      window.removeEventListener(MCP_ZONE_RESET, onReset);
      deactivate();
      count.current = 0;
      if (pulseTimer.current) {
        clearTimeout(pulseTimer.current);
        pulseTimer.current = null;
      }
      if (safetyTimer.current) {
        clearTimeout(safetyTimer.current);
        safetyTimer.current = null;
      }
    };
  }, []);

  // No DOM rendering — we manipulate the iframe + kuika_center-level overlay directly
  return null;
};
